import { formatMoney } from '@theme/utilities';
import { QuantityBreaksUpdatedEvent, ThemeEvents } from '@theme/events';

/**
 * Variantes proposées dans le bloc Toggle cross-sell : elles ne sont plus ajoutées
 * via les produits inclus / cross-sells du quantity break (comme avec le configurateur).
 * @param {HTMLElement | null | undefined} productForm
 * @returns {Set<string> | null}
 */
function getToggleCrossSellVariantExcludeSet(productForm) {
  const list = productForm?.querySelector('[data-ref="toggle-cross-sells"]');
  const raw = list?.dataset?.allVariantIds;
  if (!raw || String(raw).trim() === '') return null;
  return new Set(String(raw).split(',').map((s) => s.trim()).filter(Boolean));
}

class QuantityBreaks extends HTMLElement {
  constructor() {
    super();

    this.default_quantity_selected = null;
    this.quantity_break_items = null;
  }

  connectedCallback() {
    this.default_quantity_selected = this.dataset.quantitySelectedByDefault || 1;
    this.quantity_break_items = this.querySelectorAll('[data-ref="quantity-break-item"]');

    this.handleSubscriptionUpdate = () => this.#onSubscriptionUpdated();

    this.updateDefaultQuantitySelected();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncToggleCrossSellFromSelectedTier();
      });
    });

    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.addEventListener('click', () => {
        if (quantity_break.classList.contains('quantity-break--selected')) return;
        if (quantity_break.classList.contains('quantity-break--disabled')) return;
        this.selectQuantity(quantity_break);
      });
    });

    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated.bind(this));
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.handleSubscriptionUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.handleSubscriptionUpdate);
  }

  updateDefaultQuantitySelected() {
    this.resetSelectedQuantity();
    this.quantity_break_items[this.default_quantity_selected - 1].classList.add('quantity-break--selected');
  }

  selectQuantity(quantity_break) {
    this.resetSelectedQuantity();
    this.resetCrossSellCheckboxes();
    quantity_break.classList.add('quantity-break--selected');
    this.#syncToggleCrossSellFromSelectedTier();

    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  resetCrossSellCheckboxes() {
    this.querySelectorAll('[data-ref="quantity-break-cross-sell-checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
    });
  }

  resetSelectedQuantity() {
    this.quantity_break_items = this.querySelectorAll('[data-ref="quantity-break-item"]');
    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.classList.remove('quantity-break--selected');
    });
  }

  getSelectedQuantity() {
    return this.querySelector('.quantity-break--selected');
  }

  /**
   * Réaligne les lignes Toggle cross-sell sur le palier sélectionné + aperçu « palier suivant ».
   * À appeler après retrait manuel d’un cross-sell (même pipeline qu’après `selectQuantity`).
   */
  syncToggleCrossSellWithSelectedTier() {
    this.#syncToggleCrossSellFromSelectedTier();
  }

  getPrices() {
    return this.getSelectedQuantity().getPrices();
  }

  getCrossSellPrices() {
    const selected = this.getSelectedQuantity();
    if (!selected) return { price: 0, compareAtPrice: 0 };
    return selected.getCrossSellPrices();
  }

  getIncludedItems() {
    const selected = this.getSelectedQuantity();
    return selected?.getIncludedItems?.() ?? [];
  }

  getIncludedPrices() {
    const selected = this.getSelectedQuantity();
    return selected?.getIncludedPrices?.() ?? { price: 0, compareAtPrice: 0 };
  }

  /**
   * Centimes déjà intégrés au total du palier (lignes produits inclus) pour une variante.
   * Sert à éviter de compter deux fois le Toggle sur le bouton d’ajout au panier.
   */
  getIncludedCentContributionForVariant(variantId) {
    const selected = this.getSelectedQuantity();
    if (!selected) return { price: 0, compareAtPrice: 0 };

    const vid = String(variantId);
    let price = 0;
    let compareAtPrice = 0;

    selected.querySelectorAll('[data-ref="quantity-break-included-item"]').forEach((el) => {
      if (String(el.dataset.variantId) !== vid) return;

      const qty = parseInt(el.dataset.quantity, 10) || 1;
      const p = parseInt(el.dataset.price, 10) || 0;
      const c = parseInt(el.dataset.compareAtPrice, 10) || p;

      price += p * qty;
      compareAtPrice += c * qty;
    });

    return { price, compareAtPrice };
  }

  /**
   * Variantes listées sur Toggle cross-sell (data-all-variant-ids sur toggle-cross-sell-list).
   */
  #getToggleCrossSellVariantExcludeSet() {
    return getToggleCrossSellVariantExcludeSet(this.closest('product-form'));
  }

  /**
   * Exclut les lignes panier déjà couvertes par le Toggle cross-sell.
   */
  #filterLineItemsByToggleOverlap(items) {
    const ex = this.#getToggleCrossSellVariantExcludeSet();
    if (!ex || ex.size === 0) return items;
    return items.filter((item) => !ex.has(String(item.id)));
  }

  /**
   * Pour chaque produit inclus dans le palier qui existe aussi dans Toggle cross-sell,
   * applique la même quantité sur le Toggle (comme si le client l’avait choisie là).
   * Le prix du palier reste celui de l’offre ; la partie « incluse » ne cumule pas le prix Toggle (voir offerBundleMaxQty).
   */
  #syncToggleCrossSellFromSelectedTier() {
    const form = this.closest('product-form');
    const list = form?.querySelector('[data-ref="toggle-cross-sells"]');
    if (!list || typeof list.syncIncludedQuantitiesFromQuantityBreak !== 'function') return;

    const selected = this.getSelectedQuantity();
    if (!selected) return;

    const toggleVariantSet = getToggleCrossSellVariantExcludeSet(form);
    const includedByVariant = new Map();

    if (toggleVariantSet?.size) {
      selected.querySelectorAll('[data-ref="quantity-break-included-item"]').forEach((el) => {
        const vid = String(el.dataset.variantId);
        if (!toggleVariantSet.has(vid)) return;

        const q = parseInt(el.dataset.quantity, 10) || 1;
        const rawRp = parseInt(el.dataset.includedPriceReductionPercent ?? '100', 10);
        const reductionPercent = Number.isNaN(rawRp) ? 100 : Math.min(100, Math.max(0, rawRp));

        const prev = includedByVariant.get(vid);
        if (prev) {
          prev.qty += q;
          prev.reductionPercent = reductionPercent;
        } else {
          includedByVariant.set(vid, { qty: q, reductionPercent: reductionPercent });
        }
      });
    }

    list.syncIncludedQuantitiesFromQuantityBreak(includedByVariant);

    if (typeof list.applyNextTierPreviews === 'function') {
      list.applyNextTierPreviews();
    }
  }

  getDiscount() {
    return this.getSelectedQuantity().getDiscount();
  }

  getSelectedItems() {
    let items = [];

    if (this.isVariantSelectorActivated() && this.hasVariants()) {
      this.getSelectedQuantity()
        .querySelectorAll('[data-ref="quantity-break-variant-picker"]')
        .forEach((variant_picker) => {
          items.push({
            id: variant_picker.value,
            quantity: 1,
          });
        });
    } else {
      items.push({
        id: this.getSelectedQuantity().dataset.variantId,
        quantity: parseInt(this.getSelectedQuantity().dataset.quantity),
      });
    }

    // Ajouter les produits inclus (complémentaires), hors chevauchement Toggle cross-sell
    let includedItems = this.getSelectedQuantity().getIncludedItems?.() ?? [];
    includedItems = this.#filterLineItemsByToggleOverlap(includedItems);
    items = [...items, ...includedItems];

    // Ajouter les cross-sells cochés dans la carte QB, hors chevauchement Toggle cross-sell
    let crossSellItems = this.getSelectedQuantity().getCrossSellItems();
    crossSellItems = this.#filterLineItemsByToggleOverlap(crossSellItems);
    items = [...items, ...crossSellItems];

    return items;
  }

  /**
   * Vérifie si le selector de variantes dans le quantity break est activé
   */
  isVariantSelectorActivated() {
    return this.dataset.showVariantPicker == 'true';
  }

  hasVariants() {
    return this.dataset.hasVariants == 'true';
  }

  onVariantUpdated(event) {
    // On récupère le DOM actuel du composant
    const currentQuantityBreaks = this;

    // Le event.detail est null si l'évenement de mise à jour est déclenché par les cross-sells à activer et non par les variantes, dans ce cas là on utilise le DOM actuel
    let newQuantityBreaks = currentQuantityBreaks;
    if (event && event.detail != null) {
      newQuantityBreaks = event.detail.data.html.querySelector('[data-ref="quantity-breaks"]');
    }

    if (!newQuantityBreaks || !currentQuantityBreaks) return;

    // On remplace le DOM actuel par le nouveau DOM
    if (currentQuantityBreaks !== newQuantityBreaks) {
      currentQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]').forEach((quantity_break, index) => {
        const newQuantityBreak = newQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]')[index];
        quantity_break.render(newQuantityBreak);
      });

      // Si l'item actuellement sélectionné est maintenant désactivé, on sélectionne le premier item disponible
      this.#handleDisabledSelectedItem();

      this.dispatchEvent(new QuantityBreaksUpdatedEvent());
    }

    requestAnimationFrame(() => {
      this.#syncToggleCrossSellFromSelectedTier();
    });
  }

  /**
   * Indique si le bloc est actif (visible et pris en compte dans les calculs de prix).
   * @returns {boolean}
   */
  isActive() {
    return !this.classList.contains('quantity-breaks--hidden');
  }

  #shouldBeActive() {
    const appliesTo = this.dataset.appliesTo || 'both';
    if (appliesTo === 'both') return true;

    const subscription = this.closest('[data-ref="product-form"]')?.querySelector('[data-ref="subscription"]');
    if (!subscription || typeof subscription.isSubscriptionSelected !== 'function') return true;

    const isSubscriptionSelected = subscription.isSubscriptionSelected();

    if (appliesTo === 'one_time') return !isSubscriptionSelected;
    if (appliesTo === 'subscription') return isSubscriptionSelected;
    return true;
  }

  #onSubscriptionUpdated() {
    const wasActive = this.isActive();
    const shouldBeActive = this.#shouldBeActive();

    if (wasActive === shouldBeActive) return;

    this.classList.toggle('quantity-breaks--hidden', !shouldBeActive);

    if (!shouldBeActive) {
      this.updateDefaultQuantitySelected();
      this.resetCrossSellCheckboxes();
    } else {
      requestAnimationFrame(() => {
        this.#syncToggleCrossSellFromSelectedTier();
      });
    }

    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #handleDisabledSelectedItem() {
    const selectedItem = this.getSelectedQuantity();
    if (!selectedItem || !selectedItem.isDisabled()) return;

    // Trouver le premier item non désactivé
    const firstAvailableItem = Array.from(this.quantity_break_items).find((item) => !item.classList.contains('quantity-break--disabled'));

    if (firstAvailableItem) {
      this.resetSelectedQuantity();
      this.resetCrossSellCheckboxes();
      firstAvailableItem.classList.add('quantity-break--selected');
      this.#syncToggleCrossSellFromSelectedTier();
    }
  }
}

if (!customElements.get('quantity-breaks')) {
  customElements.define('quantity-breaks', QuantityBreaks);
}

class QuantityBreakItem extends HTMLElement {
  constructor() {
    super();

    this.variant_pickers = null;
    this.price = null;
    this.cross_sells = null;
  }

  connectedCallback() {
    this.variant_pickers = this.querySelectorAll('[data-ref="quantity-break-variant-picker"]');
    this.variant_thumbnails = this.querySelectorAll('[data-ref="quantity-break-variant-thumbnail"]');
    this.price = this.querySelector('[data-ref="quantity-break-price"]');
    this.cross_sells = this.querySelectorAll('[data-ref="quantity-break-cross-sell"]');

    this.variant_pickers.forEach((variant_picker, index) => {
      variant_picker.addEventListener('change', () => {
        this.#updateTotalPrice();
        this.#updateVariantThumbnail(variant_picker, index);

        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });
    });

    // Clic sur la ligne du cross-sell pour toggler le checkbox
    this.cross_sells.forEach((cross_sell) => {
      const checkbox = cross_sell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      const checkboxLabel = checkbox?.closest('.checkbox');

      cross_sell.addEventListener('click', (e) => {
        e.stopPropagation();

        // Toggle le checkbox si le clic n'est pas sur le label du checkbox (qui gère déjà le toggle)
        if (checkbox && !checkboxLabel?.contains(e.target)) {
          checkbox.checked = !checkbox.checked;
          this.dispatchEvent(new QuantityBreaksUpdatedEvent());
        }
      });

      checkbox?.addEventListener('change', () => {
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });
    });

    this.#updateTotalPrice();
  }

  #getShopifyCurrencyRate() {
    const raw = window.Shopify?.currency?.rate;
    if (raw === undefined || raw === null || raw === '') return 1;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  /**
   * Comparatif palier quand la variante n'a pas de compare-at mais une réduction QB s'applique.
   * @param {number} totalPrice
   * @param {number} totalCompareAtPrice
   * @param {boolean} hasPickers
   * @param {number} priceBeforeQbDiscount — somme des variantes avant réduction palier (mode picker)
   */
  #resolveCompareAtPriceForDiscount(totalPrice, totalCompareAtPrice, hasPickers, priceBeforeQbDiscount) {
    if (totalCompareAtPrice > totalPrice) return totalCompareAtPrice;

    const discountType = this.dataset.discountType;
    if (!discountType || discountType === 'none') return totalCompareAtPrice;

    const includedPrices = this.getIncludedPrices();

    if (hasPickers && priceBeforeQbDiscount > totalPrice) {
      return priceBeforeQbDiscount + includedPrices.compareAtPrice;
    }

    const qbMainCompare = parseInt(this.dataset.qbMainCompareAtPrice, 10);
    const qbMainPrice = parseInt(this.dataset.qbMainPrice, 10) || 0;
    if (qbMainCompare > qbMainPrice) {
      return qbMainCompare + includedPrices.compareAtPrice;
    }

    const rate = this.#getShopifyCurrencyRate();
    let preDiscountMain = qbMainPrice;
    if (discountType === 'value') {
      const discountValueShopCents =
        parseInt(this.dataset.discountValueShopCents || this.dataset.discountValue, 10) || 0;
      preDiscountMain += Math.round(discountValueShopCents * rate);
    } else if (discountType === 'percentage') {
      const discountPercentage = parseInt(this.dataset.discountPercentage, 10) || 0;
      if (discountPercentage > 0 && discountPercentage < 100) {
        preDiscountMain = Math.round((qbMainPrice * 100) / (100 - discountPercentage));
      }
    }

    if (preDiscountMain > totalPrice) {
      return preDiscountMain + includedPrices.compareAtPrice;
    }

    return totalCompareAtPrice;
  }

  #updateVariantThumbnail(variant_picker, index) {
    const thumbnail_container = this.variant_thumbnails[index];
    if (!thumbnail_container) return;

    const selectedOption = variant_picker.options[variant_picker.selectedIndex];
    const variantImageUrl = selectedOption.dataset.variantImage;
    const variantImageAlt = selectedOption.dataset.variantImageAlt || '';

    if (variantImageUrl) {
      thumbnail_container.classList.remove('quantity-break__variant-thumbnail--hidden');
      thumbnail_container.innerHTML = `<img src="${variantImageUrl}" alt="${variantImageAlt}" width="40" height="40" loading="lazy">`;
    } else {
      thumbnail_container.classList.add('quantity-break__variant-thumbnail--hidden');
      thumbnail_container.innerHTML = '';
    }
  }

  #updateTotalPrice() {
    let totalPrice = 0;
    let totalCompareAtPrice = 0;
    let priceBeforeQbDiscount = 0;
    const rate = this.#getShopifyCurrencyRate();

    const hasPickers = this.variant_pickers.length > 0;
    const qbMain = this.dataset.qbMainPrice;

    if (hasPickers) {
      this.variant_pickers.forEach((variant_picker) => {
        const selectedOption = variant_picker.options[variant_picker.selectedIndex];
        totalPrice += parseInt(selectedOption.dataset.price);
        if (selectedOption.dataset.compareAtPrice) {
          totalCompareAtPrice += parseInt(selectedOption.dataset.compareAtPrice);
        } else {
          totalCompareAtPrice += parseInt(selectedOption.dataset.price);
        }
      });

      priceBeforeQbDiscount = totalPrice;

      // On calcule le montant de la réduction, en fonction du type de réduction (valeur ou pourcentage)
      const discountType = this.dataset.discountType;
      const discountValueShopCents =
        parseInt(this.dataset.discountValueShopCents || this.dataset.discountValue, 10) || 0;
      const discountPercentage = parseInt(this.dataset.discountPercentage, 10) || 0;

      let totalDiscountValue = discountValueShopCents;
      if (discountType === 'percentage') {
        totalDiscountValue = (totalPrice * discountPercentage) / 100;
      } else if (discountType === 'value') {
        totalDiscountValue = Math.round(discountValueShopCents * rate);
      }

      // On soustrait le montant de la réduction au prix total (minimum 0€)
      totalPrice = Math.max(0, totalPrice - totalDiscountValue);

      // Ajouter les prix des produits inclus (complémentaires)
      const includedPrices = this.getIncludedPrices();
      totalPrice += includedPrices.price;
      totalCompareAtPrice += includedPrices.compareAtPrice;
    } else if (qbMain !== undefined && qbMain !== '') {
      // Palier sans sélecteurs dans la carte : sous-total déjà calculé côté Liquid (réduction incluse), hors produits inclus
      totalPrice = parseInt(qbMain, 10) || 0;
      totalCompareAtPrice = parseInt(this.dataset.qbMainCompareAtPrice, 10) || 0;

      if (this.dataset.discountType === 'value') {
        const discountValueShopCents =
          parseInt(this.dataset.discountValueShopCents || this.dataset.discountValue, 10) || 0;
        const discountPresentment = Math.round(discountValueShopCents * rate);
        totalPrice = Math.max(0, totalPrice - discountPresentment);
      }

      const includedPrices = this.getIncludedPrices();
      totalPrice += includedPrices.price;
      totalCompareAtPrice += includedPrices.compareAtPrice;
    } else {
      // Blocs sans data-qb-main-* : total Liquid déjà complet (ex. ancien item avec liste complémentaire)
      totalPrice = parseInt(this.dataset.totalPrice, 10) || 0;
      totalCompareAtPrice = parseInt(this.dataset.totalCompareAtPrice, 10) || 0;
    }

    totalCompareAtPrice = this.#resolveCompareAtPriceForDiscount(
      totalPrice,
      totalCompareAtPrice,
      hasPickers,
      priceBeforeQbDiscount,
    );

    // On met à jour le prix total et les datasets
    this.price.querySelector('[data-ref="price"]').innerHTML = formatMoney(totalPrice);
    this.dataset.totalPrice = totalPrice;

    const compareAtEl = this.price.querySelector('[data-ref="compare-at-price"]');

    if (this.dataset.hideCompareAt === 'true') {
      this.dataset.totalCompareAtPrice = String(totalPrice);
      if (compareAtEl) {
        compareAtEl.innerHTML = '';
        compareAtEl.dataset.value = '';
        compareAtEl.setAttribute('hidden', '');
      }
      return;
    }

    if (totalCompareAtPrice > totalPrice) {
      this.dataset.totalCompareAtPrice = totalCompareAtPrice;
      if (compareAtEl) {
        compareAtEl.innerHTML = formatMoney(totalCompareAtPrice);
        compareAtEl.dataset.value = String(totalCompareAtPrice);
        compareAtEl.removeAttribute('hidden');
      }
    } else {
      this.dataset.totalCompareAtPrice = totalPrice;
      if (compareAtEl) {
        compareAtEl.innerHTML = '';
        compareAtEl.dataset.value = '';
        compareAtEl.setAttribute('hidden', '');
      }
    }
  }

  render(newQuantityBreak) {
    // Met à jour les datasets de prix
    this.dataset.totalPrice = newQuantityBreak.dataset.totalPrice;
    this.dataset.totalCompareAtPrice = newQuantityBreak.dataset.totalCompareAtPrice;
    if (newQuantityBreak.dataset.qbMainPrice !== undefined) {
      this.dataset.qbMainPrice = newQuantityBreak.dataset.qbMainPrice;
    }
    if (newQuantityBreak.dataset.qbMainCompareAtPrice !== undefined) {
      this.dataset.qbMainCompareAtPrice = newQuantityBreak.dataset.qbMainCompareAtPrice;
    }
    this.dataset.discountTotalValue = newQuantityBreak.dataset.discountTotalValue;
    this.dataset.variantId = newQuantityBreak.dataset.variantId;

    // Met à jour les prix
    const newPriceEl = newQuantityBreak.querySelector('[data-ref="price"]');
    const newCompareEl = newQuantityBreak.querySelector('[data-ref="compare-at-price"]');
    const compareEl = this.price.querySelector('[data-ref="compare-at-price"]');

    if (newPriceEl) {
      this.price.querySelector('[data-ref="price"]').innerHTML = newPriceEl.innerHTML;
    }

    if (newCompareEl && compareEl) {
      compareEl.innerHTML = newCompareEl.innerHTML;
      if (newCompareEl.hasAttribute('hidden')) {
        compareEl.setAttribute('hidden', '');
      } else {
        compareEl.removeAttribute('hidden');
      }
      if (newCompareEl.dataset.value !== undefined) {
        compareEl.dataset.value = newCompareEl.dataset.value;
      }
    }

    // Met à jour l'image
    if (this.querySelector('[data-ref="quantity-break-image"]')) {
      this.querySelector('[data-ref="quantity-break-image"]').src = newQuantityBreak.querySelector('[data-ref="quantity-break-image"]').src;
    }

    // Met à jour l'état désactivé en fonction de la disponibilité de la nouvelle variante
    const isNewQuantityBreakDisabled = newQuantityBreak.classList.contains('quantity-break--disabled');
    this.classList.toggle('quantity-break--disabled', isNewQuantityBreakDisabled);

    this.#updateTotalPrice();
  }

  isDisabled() {
    return this.classList.contains('quantity-break--disabled');
  }

  getPrices() {
    const price = parseInt(this.dataset.totalPrice);
    const compareAtPrice = parseInt(this.dataset.totalCompareAtPrice);

    return {
      price: price,
      compareAtPrice: compareAtPrice,
    };
  }

  getCrossSellPrices() {
    return this.#getCheckedCrossSellPrices(null);
  }

  /**
   * @param {Set<string> | null | undefined} excludeSet — variantes déjà gérées par Toggle cross-sell
   */
  getCrossSellPricesExcluding(excludeSet) {
    return this.#getCheckedCrossSellPrices(excludeSet);
  }

  getIncludedItems() {
    const items = [];
    const includedElements = this.querySelectorAll('[data-ref="quantity-break-included-item"]');
    includedElements.forEach((el) => {
      items.push({
        id: el.dataset.variantId,
        quantity: parseInt(el.dataset.quantity, 10) || 1,
      });
    });
    return items;
  }

  getIncludedPrices() {
    return this.getIncludedPricesExcluding(null);
  }

  /**
   * @param {Set<string> | null | undefined} excludeSet — variantes à ne pas compter (Toggle cross-sell)
   */
  getIncludedPricesExcluding(excludeSet) {
    let price = 0;
    let compareAtPrice = 0;
    const includedElements = this.querySelectorAll('[data-ref="quantity-break-included-item"]');
    includedElements.forEach((el) => {
      if (excludeSet?.has(String(el.dataset.variantId))) return;

      const qty = parseInt(el.dataset.quantity, 10) || 1;
      price += (parseInt(el.dataset.price, 10) || 0) * qty;
      compareAtPrice += (parseInt(el.dataset.compareAtPrice, 10) || parseInt(el.dataset.price, 10) || 0) * qty;
    });
    return { price, compareAtPrice };
  }

  getCrossSellItems() {
    const items = [];

    // Récupérer dynamiquement les cross-sells pour éviter les problèmes de références
    const crossSells = this.querySelectorAll('[data-ref="quantity-break-cross-sell"]');
    crossSells.forEach((cross_sell) => {
      const checkbox = cross_sell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      if (checkbox?.checked) {
        items.push({
          id: cross_sell.dataset.variantId,
          quantity: 1,
        });
      }
    });

    return items;
  }

  /**
   * @param {Set<string> | null | undefined} excludeSet
   */
  #getCheckedCrossSellPrices(excludeSet) {
    let price = 0;
    let compareAtPrice = 0;

    const crossSells = this.querySelectorAll('[data-ref="quantity-break-cross-sell"]');
    crossSells.forEach((cross_sell) => {
      if (excludeSet?.has(String(cross_sell.dataset.variantId))) return;

      const checkbox = cross_sell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      if (checkbox?.checked) {
        const itemPrice = parseInt(cross_sell.dataset.price) || 0;
        const itemCompareAtPrice = parseInt(cross_sell.dataset.compareAtPrice) || itemPrice;
        price += itemPrice;
        compareAtPrice += itemCompareAtPrice;
      }
    });

    return { price, compareAtPrice };
  }

  getDiscount() {
    const discountValueShopCents =
      parseInt(this.dataset.discountValueShopCents || this.dataset.discountValue, 10) || 0;
    const discountRate = this.#getShopifyCurrencyRate();

    return {
      type: this.dataset.discountType || 'percentage',
      value: Math.round(discountValueShopCents * discountRate),
      percentage: parseInt(this.dataset.discountPercentage) || 0,
    };
  }
}
if (!customElements.get('quantity-break-item')) {
  customElements.define('quantity-break-item', QuantityBreakItem);
}
